import { useEffect, useState } from "react";
import { FaPlusSquare } from "react-icons/fa";
import { LuChevronsDown, LuChevronsUp } from "react-icons/lu";
import RightTimeApi from "@/API";
import { appendBillingOrgQuery } from "@/utils/billingOrgHelpers";
import PaymentEditModal from "@elements/modals/PaymentEditModal";
import Helpers from "@/Helpers";

export default function PaymentsTab(props) {
  const { isActive, updateSpinner, billingOrgId = 0 } = props;
  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [paymentsExpanded, setPaymentsExpanded] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentCompanyId, setPaymentCompanyId] = useState(null);

  useEffect(() => {
    if (isActive) 
      fetchPaymentData();
  }, [isActive, billingOrgId]);

  async function fetchPaymentData() {
    setLoading(true);
    setError(null);
    updateSpinner && updateSpinner(true);
    try {
      const resp = await RightTimeApi.getAsync(appendBillingOrgQuery("/Payments", billingOrgId));
      if (resp && resp.ok && resp.jsonData) {
        setPaymentData(resp.jsonData);
      } else {
        setError("Failed to load payment data");
        console.error("Failed to load payment data:", resp.status, resp.statusText);
      }
    } catch (error) {
      console.error("Error loading payment data:", error);
      setError("Error loading payment data: " + error.message);
    } finally {
      setLoading(false);
      updateSpinner && updateSpinner(false);
    }
  }

  function handlePaymentClick(payment = null) {
    if (payment) {
      setSelectedPayment(payment);
      setPaymentCompanyId(payment.companyId);
      setEditingPaymentId(payment.id);
    } else {
      setPaymentCompanyId(null);
      setSelectedPayment(null);
      setEditingPaymentId(0); // 0 for new payment
    }
  }

  function handlePaymentEditModalClose() {
    setEditingPaymentId(null);
    setPaymentCompanyId(null);
    setSelectedPayment(null);
    // Refresh payment data after payment recording
    if (isActive) {
      fetchPaymentData();
    }
  }

  function onToggleCollapse(e) {
    setPaymentsExpanded(!paymentsExpanded);
  }  

  // Show loading state
  if (loading) {
    return <div className="loading">Loading payment data...</div>;
  }

  // Show error state
  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  // Handle payment data - could be an array or object with payments property
  const payments = Array.isArray(paymentData) ? paymentData : paymentData?.payments || [];

  return (
    <>
      <div className="horizontal">
        <div className={`tableWrapper dimensionTable scrollable collapsable ${paymentsExpanded ? '' : 'collapsed'}`}>
          <table>
            <caption>
              <span className="title">
                Payments
                <span className="captionCount">({payments.length})</span>
              </span>
              <span className="captionRight" >
                <span className="toggleCollapse" onClick={onToggleCollapse} >
                  <LuChevronsDown className="collapse" />
                  <LuChevronsUp className="expand" />
                </span>
                <span
                  className="actionIcon plusIcon clickableIcon clickableIcon--spaced"
                  onClick={() => handlePaymentClick()}
                  title="Record Payment"
                >
                  <FaPlusSquare />
                </span>
              </span>
            </caption>
            <thead>
              <tr>
                <th>Date</th>
                <th>Company</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.length > 0 ? (
                payments.map((payment, index) => {
                  return (
                    <tr key={payment.id || index} onClick={() => handlePaymentClick(payment)} className="clickableRow">
                      <td>{Helpers.formatDate(payment.paymentDate || payment.date)}</td>
                      <td className="left">{payment.companyName || payment.company}</td>
                      <td className="right">{Helpers.formatCurrency(payment.amount)}</td>
                      <td>{payment.paymentMethod || payment.method || 'Unknown'}</td>
                      <td>{payment.reference || payment.checkNumber || payment.transactionId || ''}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="center">No payments found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PaymentEditModal
        paymentId={editingPaymentId}
        companyId={paymentCompanyId}
        paymentData={selectedPayment}
        onClose={handlePaymentEditModalClose}
        updateSpinner={updateSpinner}
      />
    </>
  );
}


